import { useAppStore } from '@store'
import { Item, LightType, Store, Visuals, Walls } from '@types'
import { serializeCameraViewForPersistence } from '@utils/camera/cameraView'
import superjson from 'superjson'
import { getProxyUrl } from '.'
import { saveImageToProject } from '@proxies/saveImageToProject'

export async function saveProyect(
  clientInfo: Store['clientInfo'],
  objetos: { idUnidades: number; zonas: Record<string, any> }[],
  totalPrice: Store['price'],
  date: string,
  visuals: Visuals,
  layers: Store['layers'],
  currLayer: number,
  items: Map<string, Item | Walls | LightType>,
  areas: Store['areas'],
  sceneBoundingBoxes: Record<string, any>,
  sceneGLB: Store['sceneGLB'],
  collisionableItems: Store['collisionableItems'],
  savedViews: Store['savedViews'],
  lastView: Store['lastView'],
): Promise<{ success: boolean; message?: string }> {
  const validIdImpuestos =
    clientInfo.idImpuestos && !isNaN(Number(clientInfo.idImpuestos))
      ? Number(clientInfo.idImpuestos)
      : null

  const escena = {
    json: superjson.stringify({
      layers,
      currLayer,
      items,
      areas,
      sceneBoundingBoxes,
      savedViews,
      collisionableItems,
      lastView: serializeCameraViewForPersistence(lastView),
    }),
    objetosEscena: {
      objetos,
      Version: 1,
    },
    visualesNombradas: visuals,
    precio: totalPrice,
  }

  const url = getProxyUrl('api/Diseno/guardar')

  const bodyObj = {
    disenyo: {
      ...clientInfo,
      idImpuestos: validIdImpuestos,
    },
    escena,
    fecha: date,
    input: 'valor_predeterminado',
  }

  const body = JSON.stringify(bodyObj)

  const params = {
    body,
    method: 'POST',
    mode: 'cors',
    headers: {
      'Accept-Language': 'ES',
      'Content-Type': 'application/json',
      Authorization: 'Ninpo ' + sessionStorage.getItem('access_token'),
    },
  }

  try {
    const response = await fetch(url, params as RequestInit)
    if (!response.ok) {
      const errorResponse = (await response.json()) as {
        detail?: string
        errors?: Record<string, string[]>
      }

      return {
        success: false,
        message:
          errorResponse.detail ||
          (errorResponse.errors
            ? Object.entries(errorResponse.errors)
                .map(([key, errors]) => `${key}: ${errors.join(', ')}`)
                .join(' | ')
            : 'Error desconocido al guardar el proyecto.'),
      }
    }

    const dataID = await response.json()
    type DataID = { idDisenos: number; idVersiones: number }
    const typedDataID = dataID as DataID

    const finalIdDisenos = typeof typedDataID.idDisenos === 'number' ? typedDataID.idDisenos : 0
    const finalIdVersiones =
      typeof typedDataID.idVersiones === 'number' ? typedDataID.idVersiones : 0

    // Verificar si la imagen se guardó correctamente después de un delay
    // El backend procesa las imágenes de forma asíncrona, así que esperamos un poco
    if (visuals && visuals.length > 0 && finalIdVersiones > 0) {
      const esActualizacion =
        clientInfo.idDisenos !== null &&
        clientInfo.idDisenos !== undefined &&
        clientInfo.idDisenos !== 0

      // Guardar referencia a la imagen nueva para comparar después
      const visual2D = visuals.find((v: any) => v.nombre === '2D')
      const imagenNuevaDataUrl = visual2D?.imageUrl || ''
      const longitudImagenNueva = imagenNuevaDataUrl.length

      // Verificar siempre, tanto en primera vez como en actualizaciones
      // Esperar un poco para que el backend procese
      setTimeout(async () => {
        try {
          const { getProxyUrl } = await import('.')
          const verifyUrl = getProxyUrl(`api/Diseno/getvisualesnombradas/${finalIdVersiones}`)
          const verifyResponse = await fetch(verifyUrl, {
            headers: {
              'Accept-Language': 'ES',
              'Content-Type': 'application/json',
              Authorization: 'Ninpo ' + sessionStorage.getItem('access_token'),
            },
          })

          if (verifyResponse.ok) {
            const verifyText = await verifyResponse.text()
            const visuales = JSON.parse(verifyText)
            const imagen2D = Array.isArray(visuales)
              ? visuales.find((v: any) => v.nombre === '2D')
              : null

            // Si hay una imagen 2D guardada en el servidor, no necesitamos guardarla de nuevo
            // Si no hay imagen 2D o es una data:image, guardamos la nueva imagen
            const tieneImagen2DEnServidor =
              imagen2D &&
              imagen2D.imageUrl &&
              !imagen2D.imageUrl.startsWith('data:image') &&
              (imagen2D.imageUrl.startsWith('http://') || imagen2D.imageUrl.startsWith('https://'))

            if (!tieneImagen2DEnServidor) {
              if (visual2D && visual2D.imageUrl && visual2D.imageUrl.length > 100) {
                await saveImageToProject(visual2D.imageUrl, finalIdVersiones, '2D')
              }
            }
          }
        } catch (error) {
          console.error('❌ Error al verificar imagen guardada:', error)
        }
      }, 2000) // Esperar 2 segundos antes de verificar
    }

    useAppStore.getState().setClientInfo({
      ...clientInfo,
      nombreProyecto: clientInfo.nombreProyecto,
      descripcion: clientInfo.descripcion,
      nombre: clientInfo.nombre,
      apellidos: clientInfo.apellidos,
      direccion: clientInfo.direccion,
      codPostal: clientInfo.codPostal,
      poblacion: clientInfo.poblacion,
      email: clientInfo.email,
      pais: clientInfo.pais,
      movil: clientInfo.movil,
      numProyectoTpc: clientInfo.numProyectoTpc,
      idDisenos: finalIdDisenos,
      idVersiones: finalIdVersiones,
      idImpuestos: validIdImpuestos ?? 0,
    })

    return { success: true }
  } catch (error) {
    console.error('Error inesperado durante la operación:', error)

    return {
      success: false,
      message: error instanceof Error ? error.message : 'Error desconocido durante la operación.',
    }
  }
}
